import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { contractsService } from '../services/contracts.service';
import { ContractStatusResponse } from '../services/types/contracts.type';
import useAuth from './useAuth';

interface ContractsContextValue {
  status: ContractStatusResponse | null;
  loading: boolean;
  showGate: boolean;
  dismissed: boolean;
  dismissForSession: () => void;
  refresh: () => Promise<void>;
}

const ContractsContext = createContext<ContractsContextValue>(
  {} as ContractsContextValue,
);

export function useContracts() {
  return useContext(ContractsContext);
}

export function ContractsProvider({ children }: { children: React.ReactNode }) {
  const { userData } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  const { data, isLoading, refetch } = useQuery<ContractStatusResponse>(
    ['contracts', 'me', 'status'],
    () => contractsService.getStatus(),
    {
      enabled: !!userData?.isAuth,
      refetchOnWindowFocus: true,
      staleTime: 60_000,
      retry: 1,
    },
  );

  const refresh = useCallback(async () => {
    await refetch();
    setDismissed(false);
  }, [refetch]);

  const dismissForSession = useCallback(() => {
    setDismissed(true);
  }, []);

  // Reset dismissed when status flips (e.g. new addendum arrives)
  const blocked = data?.blocked;
  useEffect(() => {
    if (blocked) {
      setDismissed(false); // can't dismiss when blocked
    }
  }, [blocked]);

  // Listen for the custom event dispatched by the axios interceptor when the
  // backend responds with CONTRACT_REQUIRED, so the gate modal opens immediately.
  useEffect(() => {
    if (!userData?.isAuth) return;
    const handler = () => {
      refetch();
    };
    window.addEventListener('contracts:re-check', handler);
    return () => window.removeEventListener('contracts:re-check', handler);
  }, [userData?.isAuth, refetch]);

  const showGate = !!(
    data &&
    ((data.required && !data.hasSignedContract) || data.pendingAddendum) &&
    (data.blocked || !dismissed)
  );

  return (
    <ContractsContext.Provider
      value={{
        status: data ?? null,
        loading: isLoading,
        showGate,
        dismissed,
        dismissForSession,
        refresh,
      }}
    >
      {children}
    </ContractsContext.Provider>
  );
}
